/**
 * Icon Utilities
 * Mapping of status and task types to SVG icons.
 */

/**
 * Gets the SVG icon path for a given status name.
 * @param {string} statusName - The name of the status.
 * @returns {string|null} - The path to the SVG icon in the public folder, or null if not found.
 */
/**
 * Persistent caches to keep icon assignments stable for icons.
 */
const statusIconCache = {};
const customTypeCache = {};

/**
 * Fixed icons for core task types.
 */
const FIXED_ICON_MAP = {
  bug: 'bug.svg',
  epic: 'task.svg',
  feature: 'feature.svg',
  improvement: 'improvement.svg',
  'in qa': 'testing.svg',
  'sub-task': 'task.svg',
  task: 'task.svg'
};

/**
 * Secondary pool for dynamic/unknown statuses or task types.
 * These are cycled through to ensure unique icons for custom types.
 */
const ICON_POOL = [
  'blocker.svg',
  'errorFix.svg',
  'warning.svg',
  'productionIssue.svg',
  'regressionIssue.svg',
  'cicdSetup.svg',
  'buildPipeline.svg',
  'environmentSetup.svg',
  'requirementGethering.svg',
  'sprintPlanning.svg',
  'riskManagement.svg',
  'stakeholder.svg',
  'statusReporting.svg',
  'dependencyManagement.svg',
  'detect.svg',
  'feedback.svg',
  'deployment.svg'
];

/**
 * Gets the SVG icon path for a given status name.
 * @param {string} statusName - The name of the status.
 * @returns {string} - The path to the SVG icon in the public folder.
 */
export const getStatusIcon = (statusName) => {
  const name = statusName?.toLowerCase() || '';
  
  // 1. Core status mappings (Priority keywords)
  if (name.includes('todo') || name.includes('to do') || name.includes('open')) return '/icons/task-type/todo.svg';
  if (name.includes('in progress') || name.includes('progress') || name.includes('doing')) return '/icons/task-type/inProgress.svg';
  if (name.includes('blocked')) return '/icons/task-type/blocked.svg';
  if (name.includes('review')) return '/icons/task-type/review.svg';
  if (name.includes('closed') || name.includes('done') || name.includes('complete')) return '/icons/task-type/closed.svg';
  if (name.includes('assigned')) return '/icons/task-type/assigned.svg';
  if (name.includes('hold') || name.includes('on hold')) return '/icons/task-type/onHold.svg';
  if (name.includes('testing') || name.includes('test')) return '/icons/task-type/testing.svg';
  if (name.includes('cancelled')) return '/icons/task-type/cancelled.svg';
  
  // 2. Supplementary technical and operational mappings
  if (name.includes('prod') || name.includes('production')) return '/icons/task-type/productionIssue.svg';
  if (name.includes('deployment') || name.includes('deploy')) return '/icons/task-type/deployment.svg';
  if (name.includes('qa') || name.includes('regression')) return '/icons/task-type/regressionIssue.svg';
  if (name.includes('bug')) return '/icons/task-type/bug.svg';
  if (name.includes('enhancement') || name.includes('feature')) return '/icons/task-type/feature.svg';
  if (name.includes('planning') || name.includes('sprint')) return '/icons/task-type/sprintPlanning.svg';
  if (name.includes('requirement') || name.includes('gathering')) return '/icons/task-type/requirementGethering.svg';
  if (name.includes('reporting') || name.includes('status')) return '/icons/task-type/statusReporting.svg';
  if (name.includes('risk') || name.includes('management')) return '/icons/task-type/riskManagement.svg';
  if (name.includes('stakeholder')) return '/icons/task-type/stakeholder.svg';
  if (name.includes('cicd') || name.includes('pipeline') || name.includes('setup')) return '/icons/task-type/buildPipeline.svg';
  if (name.includes('environment') || name.includes('env')) return '/icons/task-type/environmentSetup.svg';
  if (name.includes('detect') || name.includes('scan') || name.includes('security')) return '/icons/task-type/detect.svg';
  if (name.includes('dependency')) return '/icons/task-type/dependencyManagement.svg';
  if (name.includes('blocker')) return '/icons/task-type/blocker.svg';
  if (name.includes('error') || name.includes('fix')) return '/icons/task-type/errorFix.svg';
  if (name.includes('feedback')) return '/icons/task-type/feedback.svg';
  if (name.includes('warning')) return '/icons/task-type/warning.svg';
  if (name.includes('improvement')) return '/icons/task-type/improvement.svg';
  if (name.includes('backlog')) return '/icons/task-type/task.svg';
  
  // 3. Dynamic Assignment for unknown statuses (Fallback)
  if (statusIconCache[name]) {
    return `/icons/task-type/${statusIconCache[name]}`;
  }

  const usedIcons = new Set(Object.values(statusIconCache));
  const availableIcons = ICON_POOL.filter(icon => !usedIcons.has(icon));

  let assignedIcon;
  if (availableIcons.length > 0) {
    assignedIcon = availableIcons[0];
  } else {
    // Cycle through pool if exhausted
    const poolIndex = Object.keys(statusIconCache).length % ICON_POOL.length;
    assignedIcon = ICON_POOL[poolIndex];
  }

  statusIconCache[name] = assignedIcon;
  return `/icons/task-type/${assignedIcon}`;
};

export const getTaskTypeIcon = (typeName) => {
  const name = typeName?.toLowerCase().trim() || '';
  
  // 1. Check Fixed Mappings First
  if (FIXED_ICON_MAP[name]) {
    return `/icons/task-type/${FIXED_ICON_MAP[name]}`;
  }

  // 2. Check if we already assigned an icon to this custom type
  if (customTypeCache[name]) {
    return `/icons/task-type/${customTypeCache[name]}`;
  }

  // 3. Dynamic Assignment Strategy
  const usedIcons = new Set(Object.values(customTypeCache));
  const availableIcons = ICON_POOL.filter(icon => !usedIcons.has(icon));

  let assignedIcon;
  if (availableIcons.length > 0) {
    assignedIcon = availableIcons[0];
  } else {
    const poolIndex = Object.keys(customTypeCache).length % ICON_POOL.length;
    assignedIcon = ICON_POOL[poolIndex];
  }

  customTypeCache[name] = assignedIcon;
  return `/icons/task-type/${assignedIcon}`;
};
